// @ts-nocheck
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage, { siteMessageWithCustomParserOptions } from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import setFulfillmentMethod from "@insite/client-framework/Store/Context/Handlers/SetFulfillmentMethod";
import updatePickUpWarehouse from "@insite/client-framework/Store/Context/Handlers/UpdatePickUpWarehouse";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import {
    getCartState,
    getCurrentCartState,
    hasProductsWithInvalidPrice,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import setFulfillmentMethodForCart from "@insite/client-framework/Store/Data/Carts/Handlers/SetFulfillmentMethod";
import updatePickUpWarehouseForCart from "@insite/client-framework/Store/Data/Carts/Handlers/UpdatePickUpWarehouse";
import { getCurrentShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import { WarehouseModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import FindLocationModal, { FindLocationModalStyles } from "@insite/content-library/Components/FindLocationModal";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import Link from "@insite/mobius/Link";
import Modal from "@insite/mobius/Modal";
import Radio, { RadioComponentProps } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupComponentProps } from "@insite/mobius/RadioGroup";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import FieldSetPresentationProps, { FieldSetGroupPresentationProps } from "@insite/mobius/utilities/fieldSetProps";
import InfosBox, { InfosBoxProps } from "@store/Library/Components/Toolkit/InfosBox";
import { DOMNode, domToReact, HTMLReactParserOptions } from "html-react-parser";
import React, { ChangeEvent, FC } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapDispatchToProps = {
    updatePickUpWarehouse: makeHandlerChainAwaitable(updatePickUpWarehouse),
    setFulfillmentMethod,
    setFulfillmentMethodForCart,
    updatePickUpWarehouseForCart: makeHandlerChainAwaitable(updatePickUpWarehouseForCart),
};

const mapStateToProps = (state: ApplicationState) => {
    const { cartId } = state.pages.checkoutReviewAndSubmit;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const billTo = getCurrentBillToState(state).value;
    const shipTo = getCurrentShipToState(state).value;
    const customerSettings = getSettingsCollection(state).customerSettings;
    const storeOrderManagementCartSetting = getSettingsCollection(state).storeOrderManagementSettingSettings; // Store Custom Code

    return {
        cart: cartState.value,
        placeOrderErrorMessage: state.pages.checkoutReviewAndSubmit.placeOrderErrorMessage,
        hasProductsWithInvalidPrice: hasProductsWithInvalidPrice(cartState.value),
        canUpdateShipToAddress:
            billTo?.isGuest ||
            shipTo?.oneTimeAddress ||
            customerSettings.allowBillToAddressEdit ||
            customerSettings.allowShipToAddressEdit,
        updateCartInsteadOfContext: cartState.value?.isAwaitingApproval || !!cartId,
        storeOrderManagementCartSetting, // Store Custom Code
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CheckoutReviewAndSubmitErrorStyles {
    container?: GridContainerProps;
    findLocationModal?: FindLocationModalStyles;
    defaultFulfillmentMethodRadioGroup?: FieldSetGroupPresentationProps<RadioGroupComponentProps>;
    defaultFulfillmentMethodRadio?: FieldSetPresentationProps<RadioComponentProps>;
    // Starter Custom Code
    errorMessage?: InfosBoxProps;
}

export const checkoutReviewAndSubmitErrorStyles: CheckoutReviewAndSubmitErrorStyles = {
    errorMessage: { type: "error" },
};

const styles = checkoutReviewAndSubmitErrorStyles;

const CheckoutReviewAndSubmitError: FC<Props> = ({
    cart,
    placeOrderErrorMessage,
    updatePickUpWarehouse,
    setFulfillmentMethod,
    canUpdateShipToAddress,
    hasProductsWithInvalidPrice,
    updateCartInsteadOfContext,
    setFulfillmentMethodForCart,
    updatePickUpWarehouseForCart,
    storeOrderManagementCartSetting, // Store Custom Code
}) => {
    const [isFindLocationOpen, setIsFindLocationOpen] = React.useState(false);
    const openWarehouseSelectionModal = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        event.preventDefault();
        setIsFindLocationOpen(true);
    };
    const handleFindLocationModalClose = () => setIsFindLocationOpen(false);

    const toasterContext = React.useContext(ToasterContext);
    const handleWarehouseSelected = async (pickUpWarehouse: WarehouseModel) => {
        if (updateCartInsteadOfContext && cart) {
            await updatePickUpWarehouseForCart({ cart, pickUpWarehouse });
        } else {
            await updatePickUpWarehouse({ pickUpWarehouse });
        }
        toasterContext.addToast({
            body: translate("Pick Up Address Updated"),
            messageType: "success",
        });
        setIsFindLocationOpen(false);
    };

    const [isFulfillmentMethodOpen, setIsFulfillmentMethodOpen] = React.useState(false);
    const openDeliveryMethodPopup = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        event.preventDefault();
        setIsFulfillmentMethodOpen(true);
    };

    const modalCloseHandler = () => {
        setIsFulfillmentMethodOpen(false);
    };

    const fulfillmentMethodChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        if (updateCartInsteadOfContext && cart) {
            setFulfillmentMethodForCart({
                cart,
                fulfillmentMethod: event.currentTarget.value as "Ship" | "PickUp",
            });
        } else {
            setFulfillmentMethod({ fulfillmentMethod: event.currentTarget.value as "Ship" | "PickUp" });
        }
    };

    const parserOptions: HTMLReactParserOptions = {
        replace: node => {
            if (!("name" in node && "children" in node)) {
                return;
            }
            const { name, children, attribs = { style: "" }, ...rest } = node;
            const { style, class: className, ...otherAttribs } = attribs;

            otherAttribs.className = className;

            if (name === "a") {
                let onClickHandler: ((event: React.MouseEvent<HTMLElement, MouseEvent>) => void) | undefined;
                for (const key in otherAttribs) {
                    if (key === "ng-click") {
                        if (otherAttribs[key] === "vm.openWarehouseSelectionModal()") {
                            onClickHandler = openWarehouseSelectionModal;
                        }
                        if (otherAttribs[key] === "vm.openDeliveryMethodPopup()") {
                            onClickHandler = openDeliveryMethodPopup;
                        }
                    }
                }
                return (
                    <Link
                        css={style as any}
                        id={otherAttribs.name}
                        {...otherAttribs}
                        onClick={(event: React.MouseEvent<HTMLElement, MouseEvent>) =>
                            onClickHandler && onClickHandler(event)
                        }
                    >
                        {children && domToReact(children as DOMNode[], parserOptions)}
                    </Link>
                );
            }
        },
    };

    const hasInventoryError = cart?.hasInsufficientInventory === true;
    const hasAddressError = cart?.taxFailureReason === "InvalidAddress";

    if (!hasInventoryError && !placeOrderErrorMessage && !hasAddressError && !hasProductsWithInvalidPrice) {
        return null;
    }

    const { allowNotPricedOrInvalidPricingCheckout } = storeOrderManagementCartSetting; // Store Custom Code

    return (
        <>
            {hasInventoryError ? (
                <InfosBox {...styles.errorMessage}>
                    {siteMessageWithCustomParserOptions("ReviewAndPay_NotEnoughInventoryForPickup", parserOptions)}
                    <FindLocationModal
                        modalIsOpen={isFindLocationOpen}
                        onWarehouseSelected={handleWarehouseSelected}
                        onModalClose={handleFindLocationModalClose}
                        forCart
                        extendedStyles={styles.findLocationModal}
                    />
                    <Modal
                        size={500}
                        headline={translate("Fulfillment Method")}
                        isOpen={isFulfillmentMethodOpen}
                        handleClose={modalCloseHandler}
                    >
                        <RadioGroup
                            value={cart?.fulfillmentMethod}
                            onChangeHandler={fulfillmentMethodChangeHandler}
                            horizontal
                            {...styles.defaultFulfillmentMethodRadioGroup}
                        >
                            <Radio value={FulfillmentMethod.Ship} {...styles.defaultFulfillmentMethodRadio}>
                                {translate("Ship")}
                            </Radio>
                            <Radio value={FulfillmentMethod.PickUp} {...styles.defaultFulfillmentMethodRadio}>
                                {translate("Pick Up")}
                            </Radio>
                        </RadioGroup>
                    </Modal>
                </InfosBox>
            ) : null}
            {placeOrderErrorMessage ? (
                <InfosBox {...styles.errorMessage} data-test-selector="checkoutReviewAndSubmit_placeOrderErrorMessage">
                    {placeOrderErrorMessage}
                </InfosBox>
            ) : null}
            {hasAddressError ? (
                <InfosBox {...styles.errorMessage}>
                    {canUpdateShipToAddress
                        ? siteMessage("ReviewAndPay_InvalidAddress_CannotCalculateTax_UpdateAddress")
                        : siteMessage("ReviewAndPay_InvalidAddress_CannotCalculateTax")}
                </InfosBox>
            ) : null}
            {hasProductsWithInvalidPrice && !allowNotPricedOrInvalidPricingCheckout /* Store Custom Code */ ? (
                <InfosBox {...styles.errorMessage}>{siteMessage("ReviewAndPay_HasProductsWithInvalidPrice")}</InfosBox>
            ) : null}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CheckoutReviewAndSubmitError),
    definition: {
        group: "Checkout - Review & Submit",
        allowedContexts: ["CheckoutReviewAndSubmitPage"],
        displayName: "Error Messages",
        icon: "circle-exclamation",
    },
};

export default widgetModule;
